import { eventEmitter } from "../eventEmitters/EventEmitters";

// Playback feature: which mode the dashboard is currently in. Kept as a
// plain module + eventEmitter, matching this codebase's existing convention
// for interaction state (see clusterService.ts's selectedDateRange/
// selectedFilter) rather than adding a new Redux slice - the one existing
// slice (AuthReducer) holds auth/account data, not UI/interaction state.
export type PlaybackMode = "live" | "playback";

export let mode: PlaybackMode = "live";
export let playbackDate: string | null = null;
// Mirrors playbackService's playbackState.realTimeMode - kept here (not
// read directly from playbackService) so clusterService.ts can gate the
// bubble-speed setting (see applyBubbleMoveSpeed's own comment) on "true
// chronological pacing" (live OR playback's 1:1 mode) without importing
// playbackService, which would create a circular dependency (playbackService
// already imports drawUserBubble/handleVisitorMove/etc. from
// clusterService.ts). Updated by playbackService.setRealTimeMode().
export let realTimeMode = false;

/*
 * A SHARED SNIPPET (src/share/): the viewer replays a short stretch, not a day. While a window is set, playbackService treats that stretch as the "day": it starts at the window's start and is as long as the window, so every
 * part of playback (the schedule, 1:1, seeking, the end) works on it unchanged. Null everywhere else, so nothing else is affected.
 */
export interface ShareWindow {
  startMs: number;
  endMs: number;
}
export let shareWindow: ShareWindow | null = null;
/** the stretch of the day the playback scrub bar is showing right now (ms after local midnight), published by the bar so "Share a replay clip" can open on what the owner zoomed to; null when no bar is up */
export let scrubView: { from: number; to: number } | null = null;
export const setScrubView = (view: { from: number; to: number } | null) => {
  scrubView = view;
};
/** a replay clip's day-so-far (visits that had left before the clip began, as counts per dock group) and the label the dock uses for it ("Since 12:00 AM"); empty outside the viewer */
export interface PriorDayCounts {
  total: number;
  converted: number;
  margins: Record<string, Array<[string | null, number, number]>>;
}
export let priorDay: PriorDayCounts | null = null;
export let priorLabel = "";
/** the page views of every page up to the moment shown in a replay (day so far + played), for the page totals; set by playbackService */
let pageViewsProvider: (() => Array<{ name: string; overallVisitors: number }>) | null = null;
export const setPageViewsProvider = (provider: (() => Array<{ name: string; overallVisitors: number }>) | null) => {
  pageViewsProvider = provider;
};
export const playbackPageViews = (): Array<{ name: string; overallVisitors: number }> => (pageViewsProvider ? pageViewsProvider() : []);

export interface PriorFlowCounts {
  arrivals: Array<[string, number]>;
  moves: Array<[string, string, number]>;
  departures: Array<[string, number]>;
}
/** the flow lines' day-so-far for a replay clip (see clusterService.addPriorFlowCounts); null everywhere else */
export let priorFlows: PriorFlowCounts | null = null;
export const setPriorFlows = (flows: PriorFlowCounts | null) => {
  priorFlows = flows;
};
/** the most common route's tally for the day before a replay clip (see routeTally.ts WireRoute); null everywhere else */
export let priorRoutes: Array<[string[], number, number, number]> | null = null;
export const setPriorRoutes = (routes: Array<[string[], number, number, number]> | null) => {
  priorRoutes = routes;
};
export const setPriorDay = (day: PriorDayCounts | null, label = "") => {
  priorDay = day;
  priorLabel = label;
};
/** the day so far as plain departed visits for the dock's own counting, by whatever the dock is grouped by right now: each count repeated as many times, carrying only that one value (no ids, nothing to trace) */
export const priorSessionsFor = (menuType: string | undefined): any[] => {
  if (!priorDay || !priorDay.total) return [];
  const out: any[] = [];
  const rows = menuType ? priorDay.margins[menuType] : undefined;
  if (!rows) {
    for (let i = 0; i < priorDay.total; i++) out.push({ goalReached: i < priorDay.converted });
    return out;
  }
  for (const [value, n, g] of rows) {
    for (let i = 0; i < n; i++) out.push({ ...(value === null ? {} : { [menuType as string]: value }), goalReached: i < g });
  }

  return out;
};
export const setShareWindow = (window: ShareWindow | null) => {
  shareWindow = window;
};

export const setRealTimeModeFlag = (enabled: boolean) => {
  realTimeMode = enabled;
};

/*
 * WHICH PAGES A REPLAYED VISITOR HAS BEEN SHOWN SO FAR (2026-10-18). A bubble carries only the page it is on, but the funnel's spotlight needs the whole path (a visitor who landed on /sale
 * and went on to /cart never entered the funnel). playbackService.ts knows the path - it is the actions it has already applied - and registers it here, for the same reason
 * `realTimeMode` lives here: clusterService.ts cannot import playbackService without a circular dependency.
 */
let pathProvider: ((sessionId: string) => string[] | undefined) | null = null;

export const setPathProvider = (provider: (sessionId: string) => string[] | undefined) => {
  pathProvider = provider;
};

/*
 * WHERE THE REPLAY'S CLOCK IS, as an instant (epoch ms), for the funnel's numbers to describe (2026-10-18). Registered by playbackService for the same reason as the path lookup above.
 */
let clockProvider: (() => number | null) | null = null;

export const setClockProvider = (provider: () => number | null) => {
  clockProvider = provider;
};

/** The instant of the day the replay has reached (epoch ms), or null when no day is loaded. */
export const playbackClockMs = (): number | null => (clockProvider ? clockProvider() : null);

/** The pages (in order) this session has been shown during the playback so far, or undefined when playback does not know the session. */
export const pathSoFar = (sessionId: string): string[] | undefined => (pathProvider ? pathProvider(sessionId) : undefined);

/*
 * THE CURRENT PLAN RESTRICTION, if any (2026-08-27).
 *
 * Lives here rather than in a component because it is a SYSTEM-level fact -
 * "your plan does not include this" - and system messages belong in one
 * consistent place at the top of the app, not wherever the feature that
 * happened to trip the limit lives. Chris, directly: "this is a SYSTEM wide
 * message, not a playback only message. The context in the UI is off."
 *
 * Set by playbackService when a day is refused, cleared when one loads. It is
 * NOT permanently displayed: a plan limit is not an alert until you reach for
 * the thing it blocks, so the banner appears on refusal and goes away again.
 */
export let planRestrictionReason: string | null = null;

export const setPlanRestriction = (reason: string | null) => {
  if (planRestrictionReason === reason) {
    return;
  }
  planRestrictionReason = reason;
  eventEmitter.emit("planRestrictionChanged", { reason });
};

// Whether the bottom metrics bar (Footer.tsx) is currently collapsed -
// mirrored here (not just Footer.tsx's own local state + the
// "footerVisibilityChanged" event it emits on every toggle) so a component
// that mounts fresh AFTER a toggle already happened (ScrubBar.tsx only
// exists during playback, so exiting to live and re-entering unmounts and
// remounts it) can read the real current value instead of hardcoding an
// assumed default. An event fired only on CHANGE is invisible to a listener
// that starts listening after the fact - this plain module variable has no
// such timing gap.
export let footerVisible = true;

export const setFooterVisible = (visible: boolean) => {
  footerVisible = visible;
  eventEmitter.emit("footerVisibilityChanged", { visible });
};

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

// Default playback date the first time a user enters playback mode:
// "yesterday" in UTC, so something renders immediately without an extra
// click.
export const getDefaultPlaybackDate = () => {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  return toIsoDate(yesterday);
};

export const enterPlaybackMode = (date: string = getDefaultPlaybackDate()) => {
  mode = "playback";
  playbackDate = date;
  eventEmitter.emit("modeChanged", { mode, playbackDate });
};

/*
 * WATCH A SPECIFIC MOMENT (2026-10-12, the Visitors tab's "watch this visit"). Entering playback normally opens yesterday at the start; this opens a
 * given local day AND remembers where in it to start, as a ONE-SHOT request: the dashboard's mode-change handler takes it (takePlaybackFocus) when it loads
 * that day and seeks there once the day has loaded. It is a request, not state: taking it clears it, and it only applies to the day it was made for, so a
 * stale one can never move a later, unrelated playback.
 *
 * `realOffsetMs` is milliseconds since the viewer's LOCAL midnight of `date` (the same unit as the scrub bar and seekToRealOffset).
 */
export interface PlaybackFocus {
  date: string;
  realOffsetMs: number;
}

let pendingFocus: PlaybackFocus | null = null;

export const watchAt = (date: string, realOffsetMs: number) => {
  pendingFocus = { date, realOffsetMs: Number.isFinite(realOffsetMs) ? Math.max(0, realOffsetMs) : 0 };
  if (mode === "playback") {
    setPlaybackDate(date); // already in playback: just move to that day (emits modeChanged, which loads it)
  } else {
    enterPlaybackMode(date);
  }
};

export const takePlaybackFocus = (date: string): PlaybackFocus | null => {
  const focus = pendingFocus;
  pendingFocus = null;

  return focus && focus.date === date ? focus : null;
};

export const setPlaybackDate = (date: string) => {
  playbackDate = date;
  eventEmitter.emit("modeChanged", { mode, playbackDate });
};

export const exitPlaybackMode = () => {
  mode = "live";
  playbackDate = null;
  realTimeMode = false;
  /*
   * 🔴 CLEAR THE PLAN BANNER ON THE WAY OUT (2026-08-27).
   *
   * Without this the restriction survived into LIVE mode: leaving playback
   * from a refused date left "your plan replays yesterday" pinned to the top
   * of the live dashboard, where it is both irrelevant and confusing - live
   * has no date to be outside of.
   *
   * The banner is only ever SET when a playback timeline is refused, so
   * playback is also the only thing that can unset it. Caught by Chris looking
   * at the real screen and asking what the banner was.
   */
  setPlanRestriction(null);
  eventEmitter.emit("modeChanged", { mode, playbackDate });
};
